import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { styled } from '@compiled/react';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import type { ActionMeta, OptionProps, StylesConfig } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { usePreviousWithInitial } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import type { FormatMessage } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	OptionResponse,
	SelectOption,
	SelectValue,
	SelectComponentsConfig,
	SelectedValue,
} from '../../common/types.tsx';
import { isSingleValueOption } from '../../common/utils/is-single-value-option.tsx';
import type { LoadingMessages } from '../types.tsx';
import ClearSelectedItemsButton from './clear-selection-button/index.tsx';
import { MenuList } from './menu-list/main.tsx';
import { OperatorsSelect } from './operator-select/index.tsx';
import { PaginationDetails, SimplePaginationDetails } from './pagination-details/index.tsx';
import RestoreDefaultsButton from './restore-defaults-button/index.tsx';
import { singleSelectComponents, BaseSelect, selectComponents } from './select/main.tsx';
import Option from './select/option/index.tsx';
import ShowFullListButton from './show-full-list-button/index.tsx';
import type { IsError, Props } from './types.tsx';
import { generateOptions, getValueFromOptions, addFullListToOptions } from './utils.tsx';
import messages from './messages.tsx';

const MAX_TOTAL_COUNT = 1000; // To be cleaned up in EM-3568

const selectStyles: StylesConfig<SelectOption, boolean> = {
	container: (baseStyles) => ({
		...baseStyles,
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
	}),
};

const getLoadingMessage = ({
	isLoadingPaginationStart,
	isLoadingPaginationEnd,
	loadingMessages,
	formatMessage,
}: {
	isLoadingPaginationStart?: boolean;
	isLoadingPaginationEnd?: boolean;
	loadingMessages?: LoadingMessages;
	formatMessage: FormatMessage;
}) => {
	if (isLoadingPaginationStart) {
		return loadingMessages?.paginationStart || formatMessage(messages.loadingStart);
	}
	if (isLoadingPaginationEnd) {
		return loadingMessages?.paginationEnd || formatMessage(messages.loadingEnd);
	}
	return undefined;
};
export const useIsPaginationLoading = () => useState(false);

const PaginatedAsyncSelectView = ({
	id,
	onMenuScrollToBottom,
	onMenuScrollToTop,
	placeholder,
	invalidLabel,
	enableFullList: enableFullListProp,
	operandDisplayValues,
	showOperandDisplayValuesInFullList,
	loadOptions,
	onInputChange,
	value,
	onChange,
	onChangeOperator,
	onSortOptions,
	onClear,
	onRestoreDefaults,
	components: customComponents,
	showOperatorSelect,
	allowedOperators,
	selectedOperator,
	isMulti = true,
	setInitialFocusRef,
	isLoading: isLoadingExternal,
	CustomFooter,
	onKeyDown,
	loadingMessages,
	renderFooter,
	...restProps
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const paginatedComponents = useMemo(
		() => ({
			...selectComponents,
			MenuList,
		}),
		[],
	);
	const paginatedSingleSelectComponents = useMemo(
		() => ({
			...singleSelectComponents,
			Option: (props: OptionProps<SelectOption, true>) => <Option {...props} isMulti={false} />,
			MenuList,
		}),
		[],
	);
	const [isLoadingInternal, setIsLoading] = useState<boolean>(true);
	const [isPaginationLoading, setIsPaginationLoading] = useIsPaginationLoading();
	const [inputValue, setInputValue] = useState<string>('');
	const [loadedOptions, setLoadedOptions] = useState<SelectValue>([]);
	const rollbackOptionsRef = useRef(loadedOptions);
	const [enableFullList, setEnableFullList] = useState<boolean>(enableFullListProp || false);
	const [totalCount, setTotalCount] = useState<number | null>(null);
	const [endCursor, setEndCursor] = useState<string | undefined>(undefined);
	const loadingScreenMessage = useRef<string | undefined>(undefined);

	const [error, setError] = useState<IsError>({
		isError: false,
		error: null,
		onRetry: null,
	});

	const isLoading = isLoadingInternal || isLoadingExternal;

	const prevIsPaginationLoading = usePreviousWithInitial(isPaginationLoading);
	const hasFinishedPaginationLoading = prevIsPaginationLoading && !isPaginationLoading;
	loadingScreenMessage.current = getLoadingMessage({
		isLoadingPaginationStart: Boolean(isPaginationLoading && !prevIsPaginationLoading),
		isLoadingPaginationEnd: hasFinishedPaginationLoading,
		loadingMessages,
		formatMessage,
	});

	const searchRef = useRef('');
	const isColumnChangeInProgress = useRef(false);

	const itemCount = useMemo(
		() =>
			loadedOptions.flatMap((option) => (option.optionType === 'group' ? option.options : option))
				.length,
		[loadedOptions],
	);

	const getTotalCountWithFunctions = useCallback<(arg1: number | null) => number | null>(
		(newTotalCount) => {
			if (newTotalCount === null || !operandDisplayValues?.length) {
				return newTotalCount;
			}

			let newTotalCountWithKeywords: number;

			if (showOperandDisplayValuesInFullList === true) {
				newTotalCountWithKeywords = newTotalCount + operandDisplayValues.length;
			} else {
				newTotalCountWithKeywords =
					newTotalCount +
					value.filter(
						(option) => isSingleValueOption(option) && operandDisplayValues.includes(option.value),
					).length;
			}

			return newTotalCountWithKeywords < MAX_TOTAL_COUNT
				? newTotalCountWithKeywords
				: MAX_TOTAL_COUNT;
		},
		[operandDisplayValues, showOperandDisplayValuesInFullList, value],
	);

	const checkIfError = useCallback(
		(search: string, options: SelectValue, onRetry: () => void): boolean => {
			const errorOption = options.find(
				(option) => option.optionType === 'error' && option.error instanceof Error,
			);

			if (errorOption && errorOption.optionType === 'error') {
				setError({
					isError: true,
					error: errorOption.error,
					onRetry,
				});
				setIsLoading(false);
				return true;
			}

			setError({
				isError: false,
				error: null,
				onRetry: null,
			});
			return false;
		},
		[],
	);

	const handleLoadOptions = useCallback(
		(
			search: string,
			overridenValue?: SelectValue,
			renderInitialValues: boolean = enableFullList,
		): void => {
			if (!loadOptions) {
				return;
			}
			searchRef.current = search;
			setIsLoading(true);
			loadOptions(
				search,
				renderInitialValues,
				undefined,
				({ options, totalCount: newTotalCount, endCursor: newEndCursor }: OptionResponse) => {
					// Handle error
					if (
						checkIfError(search, options, () => {
							handleLoadOptions(search);
						})
					)
						return;

					// This is to prevent outdated options/results from overriding if a race condition occurs
					if (searchRef.current !== search) return;

					if (search) {
						if (!isEqual(loadedOptions, options)) {
							setLoadedOptions(options);
							setTotalCount(newTotalCount);
						}
					} else {
						const newLoadedOptions = generateOptions([...(overridenValue ?? value)], options);

						if (!isEqual(loadedOptions, newLoadedOptions)) {
							setLoadedOptions(newLoadedOptions);
							setTotalCount(getTotalCountWithFunctions(newTotalCount));
						}
					}

					setEndCursor(newEndCursor);
					setIsLoading(false);
				},
			);
		},
		[loadOptions, enableFullList, checkIfError, value, loadedOptions, getTotalCountWithFunctions],
	);

	const handleShowMore = useCallback(
		(firstLoad = false, isRetry = false) => {
			if (!loadOptions || !loadedOptions) {
				return;
			}

			let originalLoadedOptions: SelectValue;
			if (firstLoad) {
				originalLoadedOptions = [];
				setIsLoading(true);
			} else {
				originalLoadedOptions = loadedOptions;
				if (isRetry) {
					setIsLoading(true);
				} else {
					setIsPaginationLoading(true);
				}
			}

			loadOptions(
				inputValue,
				false,
				endCursor,
				({ options, totalCount: newTotalCount, endCursor: newEndCursor }) => {
					if (
						checkIfError(inputValue, options, () => {
							handleShowMore(firstLoad, true);
						})
					)
						return;

					const newLoadedOptions = inputValue
						? originalLoadedOptions.concat(options)
						: generateOptions(value, options, originalLoadedOptions, firstLoad);

					setEnableFullList(false);
					setTotalCount(inputValue ? newTotalCount : getTotalCountWithFunctions(newTotalCount));
					setLoadedOptions(newLoadedOptions);
					setIsPaginationLoading(false);
					setIsLoading(false);
					setEndCursor(newEndCursor);
				},
			);
		},
		[
			loadOptions,
			loadedOptions,
			inputValue,
			endCursor,
			checkIfError,
			value,
			getTotalCountWithFunctions,
			setIsPaginationLoading,
		],
	);

	const handleShowFullList = useCallback(() => {
		handleShowMore(true);
	}, [handleShowMore]);

	const handleInputChange = useCallback(
		(newInputValue: string) => {
			// on empty search: reset to default state for pickers where enableFullListProp==true
			if (newInputValue.length === 0 && enableFullListProp) {
				setEnableFullList(true);
				handleLoadOptions(newInputValue, undefined, true);
			} else {
				setEnableFullList(false);
				handleLoadOptions(newInputValue);
			}
			setInputValue(newInputValue);
			onInputChange && onInputChange();
		},
		[enableFullListProp, handleLoadOptions, onInputChange],
	);

	const handleOnChange = useCallback(
		(options: SelectedValue, meta: ActionMeta<SelectOption>): void => {
			if (!isColumnChangeInProgress.current && fg('jira_inline_field_config_gate')) {
				isColumnChangeInProgress.current = true;
				rollbackOptionsRef.current = loadedOptions;
			}

			if ('option' in meta && meta.option) {
				const selectedIndex = findIndex(
					loadedOptions,
					(option) =>
						isSingleValueOption(option) &&
						meta.option !== undefined &&
						isSingleValueOption(meta.option) &&
						option.value === meta.option.value,
				);

				onChange &&
					onChange(
						options,
						meta,
						{
							selectedIndex,
						},
						fg('jira_inline_field_config_gate')
							? () => {
									isColumnChangeInProgress.current = false;
								}
							: undefined,
						fg('jira_inline_field_config_gate')
							? () => {
									setLoadedOptions(rollbackOptionsRef.current);
									isColumnChangeInProgress.current = false;
								}
							: undefined,
					);
			} else {
				onChange &&
					onChange(
						options,
						meta,
						undefined,
						fg('jira_inline_field_config_gate')
							? () => {
									isColumnChangeInProgress.current = false;
								}
							: undefined,
						fg('jira_inline_field_config_gate')
							? () => {
									setLoadedOptions(rollbackOptionsRef.current);
									isColumnChangeInProgress.current = false;
								}
							: undefined,
					);
			}
		},
		[onChange, loadedOptions],
	);

	const handleOnRestoreDefaults = useCallback(() => {
		if (typeof onRestoreDefaults === 'function') {
			const defaultOptions = onRestoreDefaults();
			// Reset picker input and force update of options to go back to an initial-like state where selected options are on top
			setInputValue('');
			handleLoadOptions('', defaultOptions);
		}
	}, [handleLoadOptions, onRestoreDefaults]);

	// We only want to call this on init, so it's ok to disable react-hooks/exhaustive-deps
	useEffect(() => {
		handleLoadOptions('');

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const loadedOptionsWithFullList = useMemo(() => {
		let newLoadedOptions = loadedOptions;

		if (
			onSortOptions &&
			!fg('jira_inline_field_config_gate') &&
			fg('jira_list_merged_column_gate')
		) {
			newLoadedOptions = onSortOptions(newLoadedOptions);
		}

		return addFullListToOptions(newLoadedOptions, !enableFullList, onSortOptions);
	}, [loadedOptions, enableFullList, onSortOptions]);

	const components = useMemo<SelectComponentsConfig>(
		() =>
			isMulti
				? {
						...paginatedComponents,
						...customComponents,
					}
				: { ...paginatedSingleSelectComponents, ...customComponents },
		[customComponents, isMulti, paginatedComponents, paginatedSingleSelectComponents],
	);

	const selectedValue: SelectValue = useMemo(
		/**
		 * This method is to form the value from loadedOptionsWithFullList Array so that the 'Options' and 'Value' props of
		 * select component will have same object references, This is a workaround to meet the below condition in the '@atlaskit/select'
		 * component's ariaFocused method
		 * var isSelected = !!(focusedOption && selectValue && selectValue.includes(focusedOption));.
		 * This condition must work to announce the state of selection.
		 *
		 * The below method can be removed if the above condition is improved to check just the value instead of reference.
		 */
		() => getValueFromOptions(loadedOptionsWithFullList, value),
		[loadedOptionsWithFullList, value],
	);

	const showFooterType = useMemo(() => {
		const showFooter =
			!isLoading && loadedOptions && Boolean(loadedOptions.length) && !error.isError;

		if (showFooter) {
			if (fg('jira_inline_field_config_gate')) {
				if (
					CustomFooter != null ||
					(renderFooter != null && fg('slv-117-nin-column-picker-style-updates'))
				) {
					return 'custom';
				}
			}

			return 'default';
		}
	}, [CustomFooter, error.isError, isLoading, loadedOptions, renderFooter]);

	const handleOnKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (
				(isPaginationLoading && event.key === 'ArrowUp') ||
				(isPaginationLoading && event.key === 'ArrowDown') ||
				(isLoading && event.key === 'ArrowUp') ||
				(isLoading && event.key === 'ArrowDown')
			) {
				// prevent focusing first option when loading of more options
				event.preventDefault();
				return;
			}
			onKeyDown?.(event);
		},
		[isLoading, isPaginationLoading, onKeyDown],
	);

	const footerComponent = useMemo(() => {
		if (renderFooter && fg('slv-117-nin-column-picker-style-updates')) {
			return renderFooter(
				<SimplePaginationDetails itemsCount={itemCount} totalCount={totalCount} />,
			);
		}

		return CustomFooter;
	}, [renderFooter, CustomFooter, itemCount, totalCount]);

	return (
		<>
			{showOperatorSelect &&
				onChangeOperator &&
				allowedOperators &&
				Boolean(allowedOperators.length) && (
					<OperatorsSelect
						onChange={onChangeOperator}
						allowedOperators={allowedOperators}
						selectedOperator={selectedOperator}
					/>
				)}
			<BaseSelect
				components={components}
				inputValue={inputValue}
				onInputChange={handleInputChange}
				onMenuScrollToBottom={onMenuScrollToBottom}
				onMenuScrollToTop={onMenuScrollToTop}
				placeholder={placeholder}
				id={id}
				invalidLabel={invalidLabel}
				options={loadedOptionsWithFullList}
				setInitialFocusRef={setInitialFocusRef}
				menuListProps={{
					error,
					handleShowMore,
					isLoading,
					isPaginationLoading,
					isShowMoreEnabled: totalCount !== null && itemCount < totalCount,
				}}
				onChange={handleOnChange}
				value={selectedValue}
				styles={selectStyles}
				{...restProps}
				isMulti={isMulti}
				totalCount={totalCount}
				onKeyDown={handleOnKeyDown}
			/>
			{showFooterType === 'default' && (
				<Footer>
					{onRestoreDefaults !== undefined
						? typeof onRestoreDefaults === 'function' && (
								<RestoreDefaultsButton onRestoreDefaults={handleOnRestoreDefaults} />
							)
						: Boolean(value.length) && isMulti && <ClearSelectedItemsButton onClear={onClear} />}
					{(!value.length || !isMulti) && enableFullList && (
						<ShowFullListButton onShowFullList={handleShowFullList} />
					)}
					{!enableFullList && totalCount !== null && (
						<PaginationDetails itemsCount={itemCount} totalCount={totalCount} />
					)}
				</Footer>
			)}
			{showFooterType === 'custom' && footerComponent}
			{loadingScreenMessage.current && (
				<ScreenReaderText role="alert" aria-live="polite">
					{loadingScreenMessage.current}
				</ScreenReaderText>
			)}
			<ShortcutScope />
		</>
	);
};

export default PaginatedAsyncSelectView;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Footer = styled.div({
	paddingTop: token('space.050'),
	paddingBottom: token('space.050'),
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	borderTop: `${token('color.border')} solid 1px`,
	height: '40px',
});
